import { type CrowAppId, CrowlingoBadge, crowApp } from '@crow/ui';
import { Input } from '@crowdex/ui/components/input';
import { SegmentedControl, SegmentedControlItem } from '@crowdex/ui/components/segmented-control';
import { cn } from '@crowdex/ui/lib/utils';
import {
  ArrowLeft,
  Blocks,
  ChevronRight,
  CircleAlert,
  LibraryBig,
  type LucideIcon,
  Pin,
  Plus,
  Search,
} from 'lucide-react';
import { type PointerEvent as ReactPointerEvent, useState, useSyncExternalStore } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { useLongPress } from '@/components/mobile-shell/use-long-press';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useRouter } from '@/lib/navigation';
import { pluginsPath } from '@/lib/plugins/plugin-sections';
import { consumeDragClick, NO_WINDOW_DRAG, useFavoriteDragSource } from './favorite-drag';
import { useFavorites } from './favorites';
import { type ShellApp, useShellNavigation } from './shell-navigation';
import { FAVORITE_COLUMNS, type FavoritesDensity, LIBRARY_FAVORITE } from './sidebar-preferences';

/*
 * All apps (D3, mobile 2b): every app (Library too once it is not a favourite). Its pin (or a
 * right-click, a long press on a phone) adds it to the favourites, at the end, or takes it out;
 * on a desktop an app dragged onto the sidebar's tiles is pinned where it is dropped. It opens
 * over the workspace, like a library page; choosing an app opens it.
 */

let open = false;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const notify = () => {
  for (const listener of listeners) listener();
};

export function setAllAppsOpen(next: boolean): void {
  if (open === next) return;
  open = next;
  notify();
}

export function useAllAppsOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => open,
    () => false,
  );
}

/** Library among All apps' entries, when it is not a favourite (to add it back). */
const LIBRARY_APP: ShellApp = {
  view: LIBRARY_FAVORITE,
  title: 'Library',
  description: 'Every artifact, by app',
  icon: LibraryBig as LucideIcon,
};

/** The All apps page, over the workspace (desktop) or as a screen (phone). */
export function AllAppsPage({
  density = 'desktop',
  onLeave = (then) => then(),
}: {
  density?: 'desktop' | 'phone';
  /** A phone closes what it pushed (its history) before an app or a page opens. */
  onLeave?: (then: () => void) => void;
}) {
  const router = useRouter();
  const favorites = useFavorites();
  const { apps, isFavorite, add, remove, openApp } = favorites;
  const drag = useFavoriteDragSource(favorites);
  // The list of apps failed: say so where they would be, with Retry, not just the favourites.
  // A preview has no session by design: its apps are the boards', nothing failed (L5).
  const { preview } = useShellNavigation();
  const workbenchFailure = useWorkbench().errors[0] ?? null;
  const failure = preview ? null : workbenchFailure;
  const [query, setQuery] = useState('');
  const matches = (app: ShellApp) =>
    query.trim() === '' || app.title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase());
  // Library is offered among the apps once it is not a favourite: it can come back.
  const listed = [...(isFavorite(LIBRARY_FAVORITE) ? [] : [LIBRARY_APP]), ...apps].filter(matches);
  const toggle = (view: string) => (isFavorite(view) ? remove(view) : add(view));
  const launch = (app: ShellApp) => {
    if (app.disabled) return;
    onLeave(() => {
      setAllAppsOpen(false);
      if (app.view === LIBRARY_FAVORITE) router.push('/artifacts');
      else openApp(app.view);
    });
  };
  const browse = () =>
    onLeave(() => {
      setAllAppsOpen(false);
      router.push(pluginsPath());
    });
  if (density === 'phone')
    return (
      <div className="flex h-full min-h-0 flex-col bg-background">
        <header className="flex h-14 shrink-0 items-center gap-2 px-2 pr-4">
          <button
            aria-label="Back"
            className="flex size-10 items-center justify-center rounded-control active:bg-accent/60"
            onClick={() => setAllAppsOpen(false)}
            type="button"
          >
            <ArrowLeft className="size-5" />
          </button>
          <h1 className="type-page-title flex-1">Apps</h1>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
          <p className="type-meta text-muted-foreground">
            Long-press an app to pin it to the drawer, or to take it out.
          </p>
          <TilesPerRow className="mt-3" density="phone" favorites={favorites} />
          {failure !== null && <AppsFailure className="mt-4" error={failure} />}
          <div className="mt-4 grid grid-cols-4 gap-x-2 gap-y-4">
            {listed.map((app) => (
              <PhoneAppTile
                app={app}
                key={app.view}
                onLaunch={launch}
                onPin={toggle}
                pinned={isFavorite(app.view)}
              />
            ))}
            <button className="flex flex-col items-center gap-1.5" onClick={browse} type="button">
              <span className="flex size-14 items-center justify-center rounded-2xl border border-dashed border-hairline-strong text-muted-foreground">
                <Plus className="size-5" />
              </span>
              <span className="type-meta">Add</span>
            </button>
          </div>
          <button
            className="mt-6 flex w-full items-center gap-3 rounded-card border bg-card px-4 py-3.5 text-left"
            onClick={browse}
            type="button"
          >
            <Blocks className="size-5 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">Browse plugins</span>
              <span className="type-meta block text-muted-foreground">
                Apps, editors and services
              </span>
            </span>
            <ChevronRight className="size-4 text-muted-foreground" />
          </button>
          <p className="type-meta mt-4 text-muted-foreground">
            From here an app opens full screen. To work with one in a conversation, open it from
            that conversation's side panel.
          </p>
        </div>
      </div>
    );

  return (
    <div className="absolute inset-0 z-40 flex min-h-0 flex-col overflow-y-auto bg-background">
      <div className="mx-auto w-full max-w-[1080px] px-10 pt-6 pb-10">
        <header className="mb-6 flex items-start gap-4" data-electron-drag-region>
          <div className="min-w-0 flex-1">
            <h1 className="type-page-title">Apps</h1>
            <p className="type-meta mt-0.5 text-muted-foreground">
              Your favourites sit at the top of the sidebar: drag an app there, or pin it.
            </p>
          </div>
          <div className="flex items-center gap-4" data-electron-no-drag-region>
            <TilesPerRow density="desktop" favorites={favorites} />
            <div className="relative w-72">
              <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search apps"
                className="h-9 rounded-control bg-card pr-3 pl-10"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search apps"
                value={query}
              />
            </div>
          </div>
        </header>
        {failure !== null && <AppsFailure className="mb-3" error={failure} />}
        {/* Its apps drag onto the sidebar's tiles: never a window drag in Electron. */}
        <div
          className="grid grid-cols-3 gap-3"
          data-all-apps-list
          data-electron-no-drag-region
          style={NO_WINDOW_DRAG}
        >
          {listed.map((app) => (
            <DesktopAppCard
              app={app}
              key={app.view}
              onDragStart={(event) =>
                drag.begin(event, {
                  entry: app.view,
                  title: app.title,
                  icon: <ItemIcon className="size-[18px]" icon={app.icon} src={app.iconSrc} />,
                  from: 'apps',
                })
              }
              onLaunch={launch}
              onPin={toggle}
              pinned={isFavorite(app.view)}
            />
          ))}
          <button
            className="flex h-16 items-center gap-3 rounded-2xl border border-dashed border-hairline-strong px-5 text-left text-muted-foreground transition-colors hover:text-foreground"
            onClick={browse}
            type="button"
          >
            <Plus className="size-4" />
            <span className="font-medium">Browse plugins</span>
          </button>
        </div>
        <p className="type-meta mt-4 text-muted-foreground">
          From here an app opens full screen. To work with one in a conversation, open it from that
          conversation's side panel.
        </p>
      </div>
      {drag.ghost}
    </div>
  );
}

/** How many favourites a row of the sidebar (or the drawer) holds, in plain sight. */
function TilesPerRow({
  favorites,
  density,
  className,
}: {
  favorites: ReturnType<typeof useFavorites>;
  density: FavoritesDensity;
  className?: string;
}) {
  const label = `tiles-per-row-${density}`;
  return (
    <div className={cn('flex shrink-0 items-center gap-2', className)}>
      <span className="type-meta text-muted-foreground" id={label}>
        Tiles per row
      </span>
      <SegmentedControl
        aria-labelledby={label}
        onValueChange={(value) => favorites.setColumns(density, Number(value))}
        value={String(favorites.columns[density])}
      >
        {FAVORITE_COLUMNS[density].map((count) => (
          <SegmentedControlItem aria-label={`${count} per row`} key={count} value={String(count)}>
            {count}
          </SegmentedControlItem>
        ))}
      </SegmentedControl>
    </div>
  );
}

/** The apps could not be listed: why, and Retry (the workbench reads them again). */
function AppsFailure({ error, className }: { error: string; className?: string }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3',
        className,
      )}
      role="alert"
    >
      <CircleAlert className="size-4 shrink-0 text-destructive" />
      <p className="min-w-0 flex-1">
        <span className="font-medium">The apps could not be loaded.</span>{' '}
        <span className="text-muted-foreground">{error}</span>
      </p>
      <button
        className="flex min-h-9 shrink-0 items-center rounded-control border border-hairline-strong px-3.5 font-medium hover:bg-control"
        onClick={() => window.dispatchEvent(new Event('crowdex-plugins-changed'))}
        type="button"
      >
        Retry
      </button>
    </div>
  );
}

function DesktopAppCard({
  app,
  pinned = false,
  onLaunch,
  onPin,
  onDragStart,
}: {
  app: ShellApp;
  pinned?: boolean;
  onLaunch: (app: ShellApp) => void;
  onPin: (view: string) => void;
  /** A press that may drag it onto the sidebar's tiles (pinned where it is dropped). */
  onDragStart: (event: ReactPointerEvent<HTMLElement>) => void;
}) {
  return (
    <div
      className={cn(
        'group flex h-16 items-center gap-3.5 rounded-2xl border border-hairline bg-surface-raised px-3.5 transition-colors hover:bg-card',
        app.disabled && 'opacity-60',
      )}
    >
      <button
        className="flex min-w-0 flex-1 select-none items-center gap-3.5 text-left focus-visible:outline-none"
        disabled={app.disabled}
        onClick={() => {
          if (consumeDragClick()) return;
          onLaunch(app);
        }}
        // A right-click pins it, as a long press does on a phone.
        onContextMenu={(event) => {
          event.preventDefault();
          if (!app.disabled) onPin(app.view);
        }}
        onPointerDown={app.disabled ? undefined : onDragStart}
        type="button"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-card bg-control text-foreground/85">
          <ItemIcon className="size-5" icon={app.icon} src={app.iconSrc} />
        </span>
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-semibold">{app.title}</span>
            <AppOrigin service={app.service} />
          </span>
          <span className="type-caption block truncate text-muted-foreground">
            {pinned ? 'In the favourites' : app.description}
          </span>
        </span>
      </button>
      {!app.disabled && (
        <button
          aria-label={pinned ? `Unpin ${app.title}` : `Pin ${app.title}`}
          aria-pressed={pinned}
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-control text-subtle-foreground hover:bg-control hover:text-foreground',
            pinned && 'text-foreground',
          )}
          onClick={() => onPin(app.view)}
          type="button"
        >
          <Pin className={cn('size-3.5', pinned && 'fill-current')} />
        </button>
      )}
    </div>
  );
}

function PhoneAppTile({
  app,
  pinned = false,
  onLaunch,
  onPin,
}: {
  app: ShellApp;
  pinned?: boolean;
  onLaunch: (app: ShellApp) => void;
  onPin: (view: string) => void;
}) {
  const press = useLongPress(() => {
    if (!app.disabled) onPin(app.view);
  });
  return (
    <button
      aria-label={pinned ? `${app.title}, in the favourites` : undefined}
      className={cn('flex flex-col items-center gap-1.5', app.disabled && 'opacity-60')}
      onClick={() => onLaunch(app)}
      type="button"
      {...press}
    >
      <span className="relative flex size-14 items-center justify-center rounded-2xl bg-control">
        <ItemIcon className="size-6" icon={app.icon} src={app.iconSrc} />
        {pinned && (
          <span className="absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full border border-hairline-strong bg-card text-muted-foreground">
            <Pin className="size-3 fill-current" />
          </span>
        )}
      </span>
      <span className="type-meta max-w-full truncate">{app.title}</span>
    </button>
  );
}

/**
 * Where an app comes from, when it is a Crowlingo application's (`service` in its manifest): the
 * "Crowlingo" badge with its origin dot. It opens in a tab like any other app.
 */
export function AppOrigin({ service }: { service?: string | undefined }) {
  if (service === undefined || crowApp(service) === undefined) return null;
  return <CrowlingoBadge app={service as CrowAppId} />;
}
